import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { HiGlobe, HiUser, HiLocationMarker } from 'react-icons/hi'
import CountryFlag from '../../components/CountryFlag/CountryFlag'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair } from '../../utils/seoUtils'
import './Representatives.css'

function Representatives() {
  const { t, i18n } = useTranslation()
  const [selectedType, setSelectedType] = useState('all')
  
  const isJapanese = i18n.language === 'ja'
  const isRussian = i18n.language === 'ru'

  const breadcrumbItems = useMemo(
    () => seoBreadcrumbPair(t('common.home'), t('common.representatives'), 'representatives'),
    [t]
  )

  const types = [
    { id: 'all', name: t('representatives.all', 'All') },
    { id: 'international', name: t('representatives.international', 'International') },
    { id: 'national', name: t('representatives.national', 'National') },
    { id: 'regional', name: t('representatives.regional', 'Regional') },
    { id: 'city', name: t('representatives.city', 'City') }
  ]

  // Mock data
  const representatives = [
    {
      id: 1,
      name: isJapanese ? 'コマロフ・アントン・ヴァレリエヴィチ' : (isRussian ? 'Комаров Антон Валерьевич' : 'Komarov Anton Valeryevich'),
      nameRu: 'Комаров Антон Валерьевич',
      nameEn: 'Komarov Anton Valeryevich',
      nameJa: 'コマロフ・アントン・ヴァレリエヴィチ',
      type: 'city',
      typeRu: 'Городской',
      typeEn: 'City',
      typeJa: '都市',
      location: isJapanese ? 'ペルヴォウラリスク' : (isRussian ? 'Первоуральск' : 'Pervouralsk'),
      locationRu: 'Первоуральск',
      locationEn: 'Pervouralsk',
      locationJa: 'ペルヴォウラリスク',
      country: isJapanese ? 'ロシア' : (isRussian ? 'Россия' : 'Russia'),
      countryRu: 'Россия',
      countryEn: 'Russia',
      countryJa: 'ロシア',
      photo: '/images/persons/Komarov_Anton_Valerevich.jpg'
    },
    {
      id: 2,
      name: isJapanese ? 'ソフロノフ・ニコライ・ヴィクトロヴィチ' : (isRussian ? 'Софронов Николай Викторович' : 'Nikolai Viktorovich Sofronov'),
      nameRu: 'Софронов Николай Викторович',
      nameEn: 'Nikolai Viktorovich Sofronov',
      nameJa: 'ソフロノフ・ニコライ・ヴィクトロヴィチ',
      type: 'regional',
      typeRu: 'Региональный',
      typeEn: 'Regional',
      typeJa: '地域',
      location: isJapanese ? 'スヴェルドロフスク地方' : (isRussian ? 'Свердловская область' : 'Sverdlovsk Region'),
      locationRu: 'Свердловская область',
      locationEn: 'Sverdlovsk Region',
      locationJa: 'スヴェルドロフスク地方',
      country: isJapanese ? 'ロシア' : (isRussian ? 'Россия' : 'Russia'),
      countryRu: 'Россия',
      countryEn: 'Russia',
      countryJa: 'ロシア',
      photo: '/images/persons/Sofronov_Nikolay_Viktorovich.jpg'
    },
    {
      id: 3,
      name: isJapanese ? 'ゴレシェフ・ヴィタリー・ユーリエヴィチ' : (isRussian ? 'Голешев Виталий Юрьевич' : 'Vitaly Yuryevich Goleshev'),
      nameRu: 'Голешев Виталий Юрьевич',
      nameEn: 'Vitaly Yuryevich Goleshev',
      nameJa: 'ゴレシェフ・ヴィタリー・ユーリエヴィチ',
      type: 'national',
      typeRu: 'Национальный',
      typeEn: 'National',
      typeJa: '国家',
      location: isJapanese ? 'ベラルーシ' : (isRussian ? 'Беларусь' : 'Belarus'),
      locationRu: 'Беларусь',
      locationEn: 'Belarus',
      locationJa: 'ベラルーシ',
      country: isJapanese ? 'ベラルーシ' : (isRussian ? 'Беларусь' : 'Belarus'),
      countryRu: 'Беларусь',
      countryEn: 'Belarus',
      countryJa: 'ベラルーシ',
      photo: '/images/persons/Goleshev_Vitaliy_Yurievich.jpg'
    },
    {
      id: 4,
      name: isJapanese ? 'アッラフヴェルディエフ・タルヴェルディ' : (isRussian ? 'Аллахвердиев Тарверди' : 'Tarvardi Allahverdiev'),
      nameRu: 'Аллахвердиев Тарверди',
      nameEn: 'Tarvardi Allahverdiev',
      nameJa: 'アッラフヴェルディエフ・タルヴェルディ',
      type: 'national',
      typeRu: 'Национальный',
      typeEn: 'National',
      typeJa: '国家',
      location: isJapanese ? 'アゼルバイジャン' : (isRussian ? 'Азербайджан' : 'Azerbaijan'),
      locationRu: 'Азербайджан',
      locationEn: 'Azerbaijan',
      locationJa: 'アゼルバイジャン',
      country: isJapanese ? 'アゼルバイジャン' : (isRussian ? 'Азербайджан' : 'Azerbaijan'),
      countryRu: 'Азербайджан',
      countryEn: 'Azerbaijan',
      countryJa: 'アゼルバイジャン',
      photo: '/images/persons/Allakhverdiev_Tarverdi.jpg'
    },
    {
      id: 5,
      name: isJapanese ? 'デミトロフ・クラシミル・ミハイロフ' : (isRussian ? 'Демитров Красимир Михайлов' : 'Krasimir Mikhailov Demitrov'),
      nameRu: 'Демитров Красимир Михайлов',
      nameEn: 'Krasimir Mikhailov Demitrov',
      nameJa: 'デミトロフ・クラシミル・ミハイロフ',
      type: 'national',
      typeRu: 'Национальный',
      typeEn: 'National',
      typeJa: '国家',
      location: isJapanese ? 'ブルガリア' : (isRussian ? 'Болгария' : 'Bulgaria'),
      locationRu: 'Болгария',
      locationEn: 'Bulgaria',
      locationJa: 'ブルガリア',
      country: isJapanese ? 'ブルガリア' : (isRussian ? 'Болгария' : 'Bulgaria'),
      countryRu: 'Болгария',
      countryEn: 'Bulgaria',
      countryJa: 'ブルガリア',
      photo: '/images/persons/Dimitrov_Krasimir_Mihailov.jpg'
    },
    {
      id: 6,
      name: isJapanese ? 'グルシチェンコ・ウラジミール・レオニドヴィチ' : (isRussian ? 'Глущенко Владимир Леонидович' : 'Vladimir Leonidovich Glushchenko'),
      nameRu: 'Глущенко Владимир Леонидович',
      nameEn: 'Vladimir Leonidovich Glushchenko',
      nameJa: 'グルシチェンコ・ウラジミール・レオニドヴィチ',
      type: 'national',
      typeRu: 'Национальный',
      typeEn: 'National',
      typeJa: '国家',
      location: isJapanese ? 'ロシア' : (isRussian ? 'Россия' : 'Russia'),
      locationRu: 'Россия',
      locationEn: 'Russia',
      locationJa: 'ロシア',
      country: isJapanese ? 'ロシア' : (isRussian ? 'Россия' : 'Russia'),
      countryRu: 'Россия',
      countryEn: 'Russia',
      countryJa: 'ロシア',
      photo: '/images/persons/Glushchenko_Vladimir_Leonidovich.jpg'
    },
    {
      id: 7,
      name: isJapanese ? 'ヴァゲリス・ドロソス' : (isRussian ? 'Вагелис Дросос' : 'Vagelis Drosos'),
      nameRu: 'Вагелис Дросос',
      nameEn: 'Vagelis Drosos',
      nameJa: 'ヴァゲリス・ドロソス',
      type: 'national',
      typeRu: 'Национальный',
      typeEn: 'National',
      typeJa: '国家',
      location: isJapanese ? 'ギリシャ' : (isRussian ? 'Греция' : 'Greece'),
      locationRu: 'Греция',
      locationEn: 'Greece',
      locationJa: 'ギリシャ',
      country: isJapanese ? 'ギリシャ' : (isRussian ? 'Греция' : 'Greece'),
      countryRu: 'Греция',
      countryEn: 'Greece',
      countryJa: 'ギリシャ',
      photo: '/images/persons/Vagelis_Drosos.jpg'
    },
    {
      id: 8,
      name: isJapanese ? 'アルジャノフ・ダウレト・イサエヴィチ' : (isRussian ? 'Альжанов Даулет Исаевич' : 'Daulet Isayevich Alzhanov'),
      nameRu: 'Альжанов Даулет Исаевич',
      nameEn: 'Daulet Isayevich Alzhanov',
      nameJa: 'アルジャノフ・ダウレト・イサエヴィチ',
      type: 'national',
      typeRu: 'Национальный',
      typeEn: 'National',
      typeJa: '国家',
      location: isJapanese ? 'カザフスタン' : (isRussian ? 'Казахстан' : 'Kazakhstan'),
      locationRu: 'Казахстан',
      locationEn: 'Kazakhstan',
      locationJa: 'カザフスタン',
      country: isJapanese ? 'カザフスタン' : (isRussian ? 'Казахстан' : 'Kazakhstan'),
      countryRu: 'Казахстан',
      countryEn: 'Kazakhstan',
      countryJa: 'カザフスタン',
      photo: '/images/persons/Alzhanov_Daulet_Isaevich.jpg'
    },
    {
      id: 9,
      name: isJapanese ? 'ウスマノフ・ファフリディン' : (isRussian ? 'Усманов Фахриддин' : 'Fakhriddin Usmanov'),
      nameRu: 'Усманов Фахриддин',
      nameEn: 'Fakhriddin Usmanov',
      nameJa: 'ウスマノフ・ファフリディン',
      type: 'national',
      typeRu: 'Национальный',
      typeEn: 'National',
      typeJa: '国家',
      location: isJapanese ? 'ウズベキスタン' : (isRussian ? 'Узбекистан' : 'Uzbekistan'),
      locationRu: 'Узбекистан',
      locationEn: 'Uzbekistan',
      locationJa: 'ウズベキスタン',
      country: isJapanese ? 'ウズベキスタン' : (isRussian ? 'Узбекистан' : 'Uzbekistan'),
      countryRu: 'Узбекистан',
      countryEn: 'Uzbekistan',
      countryJa: 'ウズベキスタン',
      photo: '/images/persons/Usmanov_Fakhriddin.jpg'
    }
  ]
  
  // Фильтрация и сортировка представителей по иерархии
  const filteredRepresentatives = representatives.filter(rep => {
    return selectedType === 'all' || rep.type === selectedType
  })

  // Порядок иерархии для сортировки
  const hierarchyOrder = { 'national': 1, 'regional': 2, 'city': 3, 'international': 0 }
  
  // Группировка по типам для отображения иерархии
  const groupedRepresentatives = filteredRepresentatives.reduce((acc, rep) => {
    if (!acc[rep.type]) {
      acc[rep.type] = []
    }
    acc[rep.type].push(rep)
    return acc
  }, {})

  // Сортировка групп по иерархии
  const sortedGroups = Object.keys(groupedRepresentatives).sort((a, b) => {
    return (hierarchyOrder[a] || 999) - (hierarchyOrder[b] || 999)
  })

  // Сортировка внутри групп: Россия первая для национальных представителей
  sortedGroups.forEach(levelType => {
    if (groupedRepresentatives[levelType]) {
      groupedRepresentatives[levelType].sort((a, b) => {
        // Для национальных представителей: Россия первая
        if (levelType === 'national') {
          const aIsRussia = a.countryRu === 'Россия' || a.countryEn === 'Russia'
          const bIsRussia = b.countryRu === 'Россия' || b.countryEn === 'Russia'
          if (aIsRussia && !bIsRussia) return -1
          if (!aIsRussia && bIsRussia) return 1
        }
        // Остальные сортируются по алфавиту по стране
        const aCountry = isRussian ? (a.countryRu || a.country) : (isJapanese ? (a.countryJa || a.country) : (a.countryEn || a.country))
        const bCountry = isRussian ? (b.countryRu || b.country) : (isJapanese ? (b.countryJa || b.country) : (b.countryEn || b.country))
        return aCountry.localeCompare(bCountry)
      })
    }
  })

  // Получить название уровня
  const getLevelTitle = (type) => {
    const titles = {
      'national': { ru: 'Национальные представители', en: 'National Representatives', ja: '国家代表' },
      'regional': { ru: 'Региональные представители', en: 'Regional Representatives', ja: '地域代表' },
      'city': { ru: 'Городские представители', en: 'City Representatives', ja: '都市代表' },
      'international': { ru: 'Международные представители', en: 'International Representatives', ja: '国際代表' }
    }
    const lang = isJapanese ? 'ja' : (isRussian ? 'ru' : 'en')
    return titles[type] ? titles[type][lang] : type
  }

  // Получить код страны для флага
  const getCountryCode = (countryRu) => {
    if (!countryRu) return null
    const countryCodes = {
      'Россия': 'RU',
      'Russia': 'RU',
      'Беларусь': 'BY',
      'Belarus': 'BY',
      'Болгария': 'BG',
      'Bulgaria': 'BG',
      'Азербайджан': 'AZ',
      'Azerbaijan': 'AZ',
      'Греция': 'GR',
      'Greece': 'GR',
      'Казахстан': 'KZ',
      'Kazakhstan': 'KZ',
      'Узбекистан': 'UZ',
      'Uzbekistan': 'UZ'
    }
    const trimmed = countryRu.trim()
    return countryCodes[countryRu] || countryCodes[trimmed] || null
  }

  // Получить код страны для флага
  const getCountryCodeForFlag = (countryRu) => {
    return getCountryCode(countryRu)
  }

  return (
    <div className="representatives-page">
      <SEO 
        title={t('common.representatives')}
        description={t('representatives.description', 'Authorized representatives of SUMA International')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="representatives-hero">
          <h1>{t('common.representatives')}</h1>
          <p className="representatives-intro">
            {t('representatives.intro', 'SUMA International has authorized representatives at international, national, regional, and city levels to coordinate activities and support member schools and federations.')}
          </p>
        </div>

        <div className="representatives-structure">
          <div className="structure-item">
            <HiGlobe className="structure-icon" />
            <h3>{t('representatives.international', 'International')}</h3>
            <p>{t('representatives.internationalText', 'Representatives responsible for international coordination and development')}</p>
          </div>
          <div className="structure-item">
            <HiLocationMarker className="structure-icon" />
            <h3>{t('representatives.national', 'National')}</h3>
            <p>{t('representatives.nationalText', 'National representatives coordinating activities within countries')}</p>
          </div>
          <div className="structure-item">
            <HiLocationMarker className="structure-icon" />
            <h3>{t('representatives.regional', 'Regional')}</h3>
            <p>{t('representatives.regionalText', 'Regional representatives for specific geographic areas')}</p>
          </div>
          <div className="structure-item">
            <HiUser className="structure-icon" />
            <h3>{t('representatives.city', 'City')}</h3>
            <p>{t('representatives.cityText', 'City-level representatives for local coordination')}</p>
          </div>
        </div>

        <div className="representatives-filter">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="type-select"
          >
            {types.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div className="representatives-list">
          {filteredRepresentatives.length > 0 ? (
            sortedGroups.map(levelType => {
              const levelReps = groupedRepresentatives[levelType]
              return (
                <div key={levelType} className="representatives-level-group">
                  <h2 className={`level-title level-title-${levelType}`}>{getLevelTitle(levelType)}</h2>
                  <div className={`representatives-grid representatives-grid-${levelType}`}>
                    {levelReps.map(rep => {
                      const repName = isJapanese ? (rep.nameJa || rep.name) : (isRussian ? (rep.nameRu || rep.name) : (rep.nameEn || rep.name))
                      const repLocation = isJapanese ? (rep.locationJa || rep.location) : (isRussian ? (rep.locationRu || rep.location) : (rep.locationEn || rep.location))
                      const repCountry = isJapanese ? (rep.countryJa || rep.country) : (isRussian ? (rep.countryRu || rep.country) : (rep.countryEn || rep.country))
                      const repRanks = rep.ranks ? (isJapanese ? (rep.ranksJa || rep.ranks) : (isRussian ? (rep.ranksRu || rep.ranks) : (rep.ranksEn || rep.ranks))) : null
                      const repPosition = rep.position ? (isJapanese ? (rep.positionJa || rep.position) : (isRussian ? (rep.positionRu || rep.position) : (rep.positionEn || rep.position))) : null
                      const flagCode = getCountryCodeForFlag(rep.countryRu || rep.country)
                      
                      return (
                        <div key={rep.id} className={`representative-card representative-${rep.type}`}>
                          {rep.photo ? (
                            <div className="rep-photo">
                              <img src={rep.photo} alt={repName} />
                            </div>
                          ) : (
                            <div className="rep-photo-placeholder">
                              <HiUser />
                            </div>
                          )}
                          <h3>{repName}</h3>
                          {repPosition && (
                            <p className="rep-position">{repPosition}</p>
                          )}
                          {repRanks && (
                            <p className="rep-ranks">{repRanks}</p>
                          )}
                          <p className="rep-location">
                            <CountryFlag 
                              countryCode={flagCode}
                              className="country-flag-icon"
                              style={{ marginRight: '0.5rem', verticalAlign: 'middle', display: 'inline-block' }}
                              title={repCountry}
                            />
                            <span className="rep-location-text">
                              {repLocation === repCountry ? repCountry : `${repLocation}, ${repCountry}`}
                            </span>
                          </p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })
          ) : (
            <div className="no-representatives">
              <p>{t('representatives.noReps', 'Representatives will be displayed here as they are added to the system.')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Representatives
